import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from '@design-system/Sheet';
import { ChevronRight } from 'lucide-react';
import React from 'react';
import { useGetSupplementListQuery } from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import {
  findProductSectionsById,
  transformSupplementData,
} from '@screens/ShopPage/shopPageFunctions';
import { Loader } from '@design-system/Loader';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import {
  FormulaProduct,
  useMergedShopFormulas,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import EventTracker from '@utils/EventTracker';
import { analyticsEvent } from '@utils/analyticsEvent';

interface FormulaIngredientsSheetProps {
  ingredientCount: number;
  productId: number;
}

interface IngredientItem {
  id: number;
  is_bookmarked: boolean;
  name: string;
  volume: string | null;
}

export interface ProductSection {
  sectionTitle: string;
  productId: number;
  isBigTitle: boolean;
  items: IngredientItem[];
}

interface ProductCategory {
  title: string;
  list: ProductSection[];
}

export type SectionData = ProductCategory[];

export const FormulaIngredientsSheet: React.FC<
  FormulaIngredientsSheetProps
> = ({ productId, ingredientCount }) => {
  const {
    data: supplementListData,
    isLoading: isSupplementListLoading,
    error: isErrorInSupplementList,
  } = useGetSupplementListQuery([], []);

  const {
    data: mergedShopFormulaData,
    isLoading: isLoadingFormulas,
    isError: isErrorFormulas,
  } = useMergedShopFormulas();

  const sectionDataFromApi = transformSupplementData(
    supplementListData ?? [],
  ) as SectionData;

  const matchingProductSections = findProductSectionsById(
    sectionDataFromApi,
    productId,
  );

  const productsData: FormulaProduct[] | undefined =
    mergedShopFormulaData?.products;

  const product: FormulaProduct | undefined = productsData?.find(
    (p) => p.productId === String(productId),
  );

  if (isSupplementListLoading || isLoadingFormulas) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (isErrorInSupplementList || isErrorFormulas || !product) {
    return <ErrorLoadingPage errorType="error" hideNavBar />;
  }

  const handleTrackEvent = () => {
    if (product?.productId === '1') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_PRODUCTDETAILS_ORALLOZENGES_MYINGREDIENTS);
    } else if (product?.productId === '2') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_PRODUCTDETAILS_SUPPLEMENTS_MYINGREDIENTS);
    } else if (product?.productId === '3') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_PRODUCTDETAILS_GUTFORMULA_MYINGREDIENTS);
    } else if (product?.productId === '4') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_PRODUCTDETAILS_TOOTHPASTEANDGEL_MYINGREDIENTS);
    }
  };

  return (
    <Sheet>
      <SheetTrigger onClick={handleTrackEvent}>
        <button
          data-product={product?.productId}
          className="flex items-center rounded-md pl-4 pr-2 py-3 typography-btn-medium text-secondary justify-center transition-colors bg-gradient-to-b data-[product=2]:from-[#FCF2EB] data-[product=2]:to-[#FCF9E3] data-[product=3]:from-[#FCF2EB] data-[product=3]:to-[#FCF2EB] data-[product=1]:from-[#E9F3FC] data-[product=1]:to-[#E9F3FC] from-[#EBFAF8] to-[#EBFAF8]"
        >
          My {ingredientCount} Ingredients{' '}
          <ChevronRight className="text-secondary-foreground h-4 w-4 mt-0.5 ml-1"/>
        </button>
      </SheetTrigger>
      <SheetContent className="w-full md:w-[400px]">
        <SheetHeader className="pt-4 pb-2 px-6">
          <br/><br/>
          <div>
            <p className="typography-h1 text-primary mb-4">
              {ingredientCount} Ingredients
            </p>
            {productId === 2 && (
              <p className="typography-body text-tertiary">8 capsules daily</p>
            )}
          </div>
        </SheetHeader>
        <div className="p-6 overflow-y-auto">
          {matchingProductSections.length > 0 ? (
            matchingProductSections.map((section, sectionIndex) => (
              <div
                key={`section-${section.productId}-${sectionIndex}-${section.sectionTitle || 'default'}`}
                className="mb-6 last:mb-0"
              >
                {section.sectionTitle && section.items.length > 0 && (
                  <div className="mb-4">
                    <p className="typography-h3 text-primary">
                      {section.sectionTitle}
                    </p>
                    <hr className='mt-6 border-secondary border-[1.5px]'/>
                  </div>
                )}
                <div>
                {(section.items || []).map((ingredient, ingredientIndex) => (
                    <div
                      key={`${ingredient.id}-${ingredientIndex}`}
                      className="flex justify-between items-center py-3 border-b border-secondary last:border-b-0"
                    >
                      <p className="typography-title text-secondary flex-1 mr-4">
                        {ingredient.name}
                      </p>
                      <p className="typography-body text-secondary">
                        {ingredient.volume ?? ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center typography-body text-gray-500 mt-4">
              No specific ingredients listed for this product.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
